import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Pencil,
  Trash2,
  Check,
  X,
  Building2,
  Clock,
  ListChecks,
  BellRing,
  StickyNote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { JournalEntryDTO } from "@/types/journalEntry";
import { journalEntryService } from "@/services/journalEntryService";
import {
  JournalActionFields,
  JournalActionValue,
  toReminderInputValue,
  toReminderIso,
} from "@/components/admin/journal/JournalActionFields";

interface JournalEntryItemProps {
  entry: JournalEntryDTO;
  /**
   * When true, shows the event's company name + date as a link to the event, or
   * labels the entry as a personal reminder when it belongs to no event.
   */
  showEventLink?: boolean;
  onUpdated: (entry: JournalEntryDTO) => void;
  onDeleted: (id: string) => void;
}

export default function JournalEntryItem({
  entry,
  showEventLink = false,
  onUpdated,
  onDeleted,
}: JournalEntryItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(entry.content);
  const [followUpDraft, setFollowUpDraft] = useState<JournalActionValue>({
    action: entry.action ?? "",
    reminderAt: toReminderInputValue(entry.reminderAt),
  });
  const [busy, setBusy] = useState(false);

  /** Puts the editor back to what is actually saved. */
  const resetDrafts = () => {
    setDraft(entry.content);
    setFollowUpDraft({
      action: entry.action ?? "",
      reminderAt: toReminderInputValue(entry.reminderAt),
    });
  };

  const handleSave = async () => {
    if (!draft.trim()) {
      toast({
        title: "Entry can't be empty",
        variant: "destructive",
      });
      return;
    }
    setBusy(true);
    try {
      const updated = await journalEntryService.update(entry.id, {
        content: draft.trim(),
        action: followUpDraft.action.trim() || null,
        reminderAt: toReminderIso(followUpDraft.reminderAt),
      });
      onUpdated(updated);
      setIsEditing(false);
      toast({ title: "Entry updated" });
    } catch {
      toast({ title: "Failed to update entry", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this journal entry? This can't be undone.")) {
      return;
    }
    setBusy(true);
    try {
      await journalEntryService.remove(entry.id);
      onDeleted(entry.id);
      toast({ title: "Entry deleted" });
    } catch {
      toast({ title: "Failed to delete entry", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-0">
      <CardContent className="p-3 space-y-1.5">
        {showEventLink && !entry.eventId && (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
            <StickyNote className="h-4 w-4 shrink-0" />
            Personal reminder
          </span>
        )}

        {showEventLink && entry.eventId && (
          <Link
            to={`/admin/events/${entry.eventId}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:underline"
          >
            <Building2 className="h-4 w-4 shrink-0" />
            {entry.eventCompanyName}
            {entry.eventStartTime && (
              <span className="text-muted-foreground font-normal">
                · {format(new Date(entry.eventStartTime), "MMM d, yyyy")}
              </span>
            )}
          </Link>
        )}

        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              autoFocus
            />
            <JournalActionFields
              idPrefix={`entry-${entry.id}`}
              value={followUpDraft}
              onChange={setFollowUpDraft}
              disabled={busy}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={busy}>
                <Check className="mr-1.5 h-4 w-4" />
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  resetDrafts();
                  setIsEditing(false);
                }}
                disabled={busy}
              >
                <X className="mr-1.5 h-4 w-4" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <p className="whitespace-pre-wrap break-words text-sm">
              {entry.content}
            </p>
            {(entry.action || entry.reminderAt) && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {entry.action && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2 py-1 font-medium">
                    <ListChecks className="h-3.5 w-3.5 shrink-0" />
                    {entry.action}
                  </span>
                )}
                {entry.reminderAt && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2 py-1 text-muted-foreground">
                    <BellRing className="h-3.5 w-3.5 shrink-0" />
                    {format(new Date(entry.reminderAt), "MMM d, yyyy · h:mm a")}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {format(new Date(entry.createdAt), "MMM d, yyyy · h:mm a")} —{" "}
              {entry.authorName}
            </span>
          </div>
          {!isEditing && (
            <div className="flex shrink-0">
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => {
                  resetDrafts();
                  setIsEditing(true);
                }}
                disabled={busy}
                aria-label="Edit entry"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive"
                onClick={handleDelete}
                disabled={busy}
                aria-label="Delete entry"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
